import { ComponentDecisions } from '../structure';

export interface StatefulDecision {
    default: string;
    hover: string;
    active: string;
    focus: string;
    disabled: string;
}

export interface DecisionWithInverse {
    default: string;
    inverse: string;
}

export interface SemanticDecisions {
    /**
     * Decisions that have multiple states (hover, active, ...),
     * Used in components with interactions, mainly buttons, can be used without limitations - backgrounds, borders, text colors, ...
     * */
    interactive: {
        basic: StatefulDecision;
        basicSecondary: {
            default: StatefulDecision;
            contrast: StatefulDecision;
        };
        primary: StatefulDecision;
        info: StatefulDecision;
        success: StatefulDecision;
        warning: StatefulDecision;
        destructive: StatefulDecision;
        plain: {
            basic: StatefulDecision;
            contrast: StatefulDecision;
        };
        bold: {
            basic: StatefulDecision;
            contrast: StatefulDecision;
        };
    };

    /**
     * Used only for backgrounds - surfaces, icons, etc., not for border or text.
     */
    backgroundColor: {
        /**
         * Used only for body of the application, nothing else.
         */
        body: {
            basic: string;
        };

        /**
         * Used for surfaces like appbar, cards, overlays or any other background of components
         */
        surface: {
            basic: DecisionWithInverse;
            thin: {
                default: string;
                contrast: string;
            };
            extraLight: string;
            light: string;
            medium: {
                default: string;
                contrast: string;
            };
            emphasized: string;
            highlighted: string;
        };

        /**
         * Used for surfaces that give meaning to the component, for example supplementary icons or system-wide banners.
         */
        semantic: {
            basic: string;
            primary: string;
            info: string;
            success: string;
            warning: string;
            destructive: string;
        };

        /**
         * Faint version of semantic colors, used in components that have same background no matter the underlaying color
         */
        semanticSubtle: {
            basic: string;
            primary: string;
            info: string;
            success: string;
            warning: string;
            destructive: string;
        };

        /**
         * Faint version of semantic colors using transparency, used in cases where background color should blend with underlying color
         */
        semanticSubtleTransparent: {
            basic: string;
            primary: string;
            success: string;
            info: string;
            warning: string;
            destructive: string;
        };

        /**
         * Faint version of semantic secondary colors using transparency, used in cases where background color should blend with underlying color
         */
        semanticSecondarySubtleTransparent: {
            basic: string;
            primary: string;
            success: string;
            info: string;
            warning: string;
            destructive: string;
        };

        /**
         * Used strictly with a semantic background to maintain proper contrast
         */
        onSemanticBackground: {
            light: {
                basic: string;
                transparent: string;
            };
            dark: {
                basic: string;
                transparent: string;
            };
        };

        /**
         * Used in cases where we don't want to change color based on theme
         */
        contrast: {
            light: string;
            dark: string;
        };

        /**
         * Used for category visualizations and other usecases where semantic colors don't make sense.
         * These colors don't have semantics, it's provided by the usage in the products.
         */
        category: {
            mustard: string;
            tangerine: string;
            lavender: string;
            denim: string;
            lime: string;
            ruby: string;
        };

        /**
         * Used for category visualizations and other usecases where semantic colors don't make sense.
         * These colors don't have semantics, it's provided by the usage in the products.
         */
        categorySubtle: {
            mustard: string;
            tangerine: string;
            lavender: string;
            denim: string;
            lime: string;
            ruby: string;
        };

        /**
         * Used mainly for backdrops and similar big transparent surfaces
         */
        backdrop: string;
    };

    /**
     * Used only for text, nothing else.
     */
    textColor: {
        /**
         * Used for typography components.
         */
        font: {
            basic: {
                default: string;
                inverse: string;
                contrast: string;
            };
            secondary: {
                default: string;
                contrast: string;
            };
            tertiary: {
                default: string;
                contrast: string;
            };
        };

        /**
         * Used for colors that give the meaning to the text.
         */
        semantic: {
            basic: string;
            primary: string;
            success: string;
            info: string;
            warning: string;
            destructive: string;
        };

        /**
         * More visible variant of semantic colors for better contrast.
         */
        semanticStrong: {
            basic: string;
            primary: string;
            success: string;
            info: string;
            warning: string;
            destructive: string;
        };

        /**
         * Used strictly with a semantic background to maintain proper contrast of a text
         */
        onSemanticBackground: {
            light: string;
            dark: string;
        };

        /**
         * Used in cases where we don't want to change color based on theme
         */
        contrast: {
            light: string;
            dark: string;
        };

        /**
         * Used for category visualizations and other usecases where semantic colors don't make sense.
         * These colors don't have semantics, it's provided by the usage in the products.
         */
        category: {
            mustard: string;
            tangerine: string;
            lavender: string;
            denim: string;
            lime: string;
            ruby: string;
        };

        /**
         * More visible variant of category colors for better contrast.
         */
        categoryStrong: {
            mustard: string;
            tangerine: string;
            lavender: string;
            denim: string;
            lime: string;
            ruby: string;
        };
    };

    /**
     * Used only for borders of the components, nothing else.
     */
    borderColor: {
        /**
         * Used for borders that give meaning to the component.
         */
        semantic: {
            basic: string;
            primary: string;
            info: string;
            success: string;
            warning: string;
            destructive: string;
        };

        /**
         * Faint version of semantic borders.
         */
        semanticSubtle: {
            basic: string;
            primary: string;
            info: string;
            success: string;
            warning: string;
            destructive: string;
        };

        /**
         * Used for category visualizations and other usecases where semantic colors don't make sense.
         * These colors don't have semantics, it's provided by the usage in the products.
         */
        categorySubtle: {
            mustard: string;
            tangerine: string;
            lavender: string;
            denim: string;
            lime: string;
            ruby: string;
        };

        /**
         * Used for image borders to differentiate them from background.
         */
        image: {
            transparent: string;
        };

        interactive: {
            basic: StatefulDecision;
        };

        divider: {
            basic: string;
        };

        container: string;
    };
}

export type CreateDecisions<T extends string = ''> = ({
    decisions,
    overrides,
}: {
    decisions: SemanticDecisions;
    overrides?: ComponentDecisions<T>;
}) => ComponentDecisions<T>;
